import {
  claimPageLoadRecovery,
  createPageLoadDiagnostic,
  flushPendingPageLoadDiagnostic,
  queuePageLoadDiagnostic,
  type PageLoadDiagnostic,
} from './pageLoadDiagnostics';

export const ASSET_LOAD_RECOVERY_STORAGE_KEY = 'zeroth:page-load-recovery';

interface AssetLoadRecoveryOptions {
  reload?: () => void;
  storage?: Storage;
  loadReleaseSha?: () => Promise<string | null>;
}

function getPreloadError(event: Event): Error {
  const payload = (event as CustomEvent<unknown>).detail
    ?? (event as Event & { payload?: unknown }).payload;
  if (payload instanceof Error) return payload;
  return new Error('Failed to fetch dynamically imported module');
}

async function loadCurrentReleaseSha(): Promise<string | null> {
  try {
    const response = await fetch('/release.json', {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const payload = await response.json() as { releaseSha?: unknown };
    return typeof payload.releaseSha === 'string' && /^[0-9a-f]{40}$/.test(payload.releaseSha)
      ? payload.releaseSha
      : null;
  } catch {
    return null;
  }
}

export async function attemptAssetLoadRecovery(
  diagnostic: PageLoadDiagnostic,
  options: AssetLoadRecoveryOptions = {},
): Promise<boolean> {
  const releaseSha = await (options.loadReleaseSha ?? loadCurrentReleaseSha)();
  if (!releaseSha) return false;
  if (!claimPageLoadRecovery(diagnostic, options.storage)) return false;
  (options.reload ?? (() => window.location.reload()))();
  return true;
}

export function installAssetLoadRecovery(
  targetWindow: Window | undefined = typeof window === 'undefined' ? undefined : window,
  options: AssetLoadRecoveryOptions = {},
): () => void {
  if (!targetWindow) return () => undefined;
  const recoveriesInFlight = new Map<string, Promise<boolean>>();

  const handlePreloadError = (event: Event): void => {
    const error = getPreloadError(event);
    const route = targetWindow.location.pathname;
    const diagnostic = createPageLoadDiagnostic({
      error,
      componentStack: '',
      route,
      recoveryAttempt: 0,
      category: 'asset',
    });
    queuePageLoadDiagnostic({
      error,
      componentStack: '',
      route,
      recoveryAttempt: 0,
      category: 'asset',
      storage: options.storage,
    });
    void flushPendingPageLoadDiagnostic(options.storage);
    const recoveryKey = `${diagnostic.route}:${diagnostic.frontendSha}:${diagnostic.fingerprint}`;
    if (!recoveriesInFlight.has(recoveryKey)) {
      const recovery = attemptAssetLoadRecovery(diagnostic, options);
      recoveriesInFlight.set(recoveryKey, recovery);
      void recovery.finally(() => {
        recoveriesInFlight.delete(recoveryKey);
      });
    }
  };

  targetWindow.addEventListener('vite:preloadError', handlePreloadError);
  return () => targetWindow.removeEventListener('vite:preloadError', handlePreloadError);
}
